/**
 * TopBannerSlot — floating sponsor pill anchored top-right of the player.
 *
 * Per Figma: "During play: 5 seconds into clip, or as playback begins, upper
 * right unit will slide in from right side, X will appear after 3 seconds,
 * for user to dismiss. Can also be a display banner and can be non-dismissable."
 *
 * Layout: [iconTile] [title / subtitle] [CTA pill] [× close]
 *
 * Mounted as an absolute overlay inside <BrandedPlayer> so it sits on top of
 * the video — not as a sibling row that pushes the player down.
 */
import { useRecoilState, useRecoilValue } from "recoil";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";

import { SponsorLogo } from "../SponsorAssets";
import {
  BrandedFullscreenState,
  BrandedLayoutVariantState,
  BrandedPlaybackElapsedMsState,
  BrandedPlaybackPhaseState,
  BrandedSponsorConfigState,
  BrandedTopBannerDismissedState,
  BrandedViewerGeoState,
} from "../../State";
import {
  computeTopBannerDismissReady,
  computeTopBannerVisible,
} from "../../ViewModel";
import { fireAdClickBeacon, useImpressionBeacon } from "../../tracking";

/* Size tier picker for the banner pieces. Three tiers:
   - $fs (fullscreen)         : largest — reads at TV viewing distance
   - $bp (bottom-playlist)    : medium — embed runs the player at full
                                iframe width so the banner has more
                                budget than the side-rail variant
   - default (side-rail embed): smallest — banner is a corner sliver
   The ternary chain runs $fs first because it's the most-extreme
   override; $bp falls through when not fullscreen. */
const pickTier = <T,>(
  $fs: boolean | undefined,
  $bp: boolean | undefined,
  fs: T,
  bp: T,
  base: T,
): T => ($fs ? fs : $bp ? bp : base);

const BannerShell = styled(Flex)<{
  $visible: boolean;
  $fs?: boolean;
  $bp?: boolean;
}>`
  align-items: center;
  /* rgb(12,19,24) is the #0c1318 canvas color; banner is a translucent
     pane of that same canvas tone sitting over the video. */
  background: rgba(12, 19, 24, 0.55);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.45);
  /* Tuned for HLTV's narrow in-page embed. Prior 12/10/16 sizing left
     the title text truncated at "Unl..." with the CTA pill squashed
     into the close button. Right padding is tighter than left so the
     CTA + × action group rides closer to the right edge — the icon
     tile on the left sets the visual anchor and the action cluster
     reads as anchored to the opposite edge. */
  /* Sizing flexes between embed-scale and fullscreen-scale via the
     $fs prop (driven by BrandedFullscreenState). All inner pieces
     (title, subtitle, CTA, dismiss, icon tile) read the same flag and
     scale together. */
  /* All sizing below carries a global ~1.33x bump (June 2026 review:
     "scale all parts up ~33%") applied uniformly across the three
     tiers. Anchoring insets (right/top) intentionally NOT scaled so
     the bigger unit stays pinned to the same corner. */
  gap: ${({ $fs, $bp }) => pickTier($fs, $bp, "16px", "13px", "7px")};
  padding: ${({ $fs, $bp }) =>
    pickTier(
      $fs,
      $bp,
      "16px 13px 16px 21px",
      "12px 13px 12px 19px",
      "4px 3px 4px 5px",
    )};
  position: absolute;
  right: ${({ $fs, $bp }) => pickTier($fs, $bp, "24px", "16px", "6px")};
  top: ${({ $fs, $bp }) => pickTier($fs, $bp, "24px", "16px", "6px")};
  z-index: 6;
  border-radius: ${({ $fs, $bp }) =>
    pickTier($fs, $bp, "24px", "19px", "13px")};
  /* Embed cap keeps the banner a corner sliver; fullscreen + bottom-
     playlist caps are looser so the bigger content has room.
     Bottom-playlist runs the player at full iframe width, so the banner
     can stretch nearly half the player width before it starts crowding
     the upper-left watermark — publisher feedback was that the previous
     ~340px cap still read as a corner sticker, not as the headline ad
     slot it's supposed to be. */
  max-width: ${({ $fs, $bp }) =>
    pickTier(
      $fs,
      $bp,
      "min(745px, 40%)",
      /* +25% bump per Nick — the previous 60% cap still read as a
         "corner sticker" against the wider full-bleed player. Stays
         pinned top-right (no centering — must not overlap the upper-
         left watermark). Absolute px cap also bumps so the banner can
         actually use the wider canvas at the 638w iframe. */
      "min(865px, 85%)",
      "min(306px, 82%)",
    )};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transform: translateX(${({ $visible }) => ($visible ? "0" : "24px")});
  transition:
    transform 360ms ease,
    opacity 360ms ease;

  /* Fullscreen: dramatically enlarge the banner via padding, gap and
     children font-sizes (NOT transform — that would clobber the
     slide-in translateX). max-width unclamped so the larger content
     can breathe. Inner pieces are nudged via the parent's :fullscreen
     descendant selectors below. */
  :fullscreen & {
    border-radius: 24px;
    gap: 16px;
    padding: 16px 13px 16px 21px;
    right: 24px;
    top: 24px;
    max-width: min(745px, 40%);
  }

  /* Mobile: player is much shorter (16:9 of full-width iframe), so the
     banner gets one more shrink pass to stay clear of the intro
     overlay's headline and not eat too much horizontal real estate.
     Capped narrower so it stays a sliver at the top-right corner. */
  @media (max-width: 560px) {
    max-width: min(293px, 70%);
    padding: 5px 5px 5px 8px;
    gap: 8px;
    right: 6px;
    top: 6px;
  }
`;

/**
 * Image-only banner shell. Used when the campaign uploaded a complete
 * pre-composed creative (gif/png/jpg) and left both `title` and
 * `ctaLabel` empty — the iframe skips the icon-tile + rich-text + CTA
 * composition and slides the uploaded asset in as the entire creative,
 * acting as a single clickable banner. Size tiers track <BannerShell>
 * so the same corner-sliver / fullscreen / bottom-playlist breakpoints
 * apply.
 */
const ImageOnlyShell = styled.a<{
  $visible: boolean;
  $fs?: boolean;
  $bp?: boolean;
}>`
  display: block;
  position: absolute;
  right: ${({ $fs, $bp }) => pickTier($fs, $bp, "24px", "16px", "6px")};
  /* Image-only banners sit 15px higher than the rich BannerShell — the
     uploaded creative often has its own visual padding baked in, and
     the +15 lift keeps it from feeling like it's sagging into the
     player area. (Rich BannerShell stays at its tighter top so the
     CTA pill doesn't cover the upper-left watermark.) */
  top: ${({ $fs, $bp }) => pickTier($fs, $bp, "9px", "1px", "0px")};
  z-index: 6;
  max-width: ${({ $fs, $bp }) =>
    pickTier(
      $fs,
      $bp,
      "min(745px, 40%)",
      "min(865px, 85%)",
      "min(306px, 82%)",
    )};
  /* Image-only mode is raw — no chrome (no border-radius mask, no
     drop shadow, no backdrop blur). The uploaded asset IS the
     creative; any decoration we add visually alters it from what the
     sponsor saw in the campaign editor preview. */
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transform: translateX(${({ $visible }) => ($visible ? "0" : "24px")});
  transition:
    transform 360ms ease,
    opacity 360ms ease;
  text-decoration: none;

  & > img {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: 560px) {
    max-width: min(293px, 70%);
    right: 6px;
    /* Mobile mirrors the desktop lift: image-only sits flush against
       the top of the player, since the asset already carries its own
       visual padding. */
    top: 0;
  }
`;

const ImageOnlyDismissBtn = styled.button<{ $ready: boolean }>`
  align-items: center;
  appearance: none;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  font-size: 19px;
  height: 29px;
  justify-content: center;
  line-height: 1;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  pointer-events: ${({ $ready }) => ($ready ? "auto" : "none")};
  position: absolute;
  right: 6px;
  top: 6px;
  transition: opacity 200ms ease;
  width: 29px;
  z-index: 1;
`;

const IconTileSlot = styled(Box)<{ $fs?: boolean; $bp?: boolean }>`
  display: flex;
  flex: 0 0 auto;

  /* Override the JSX-prop'd 18x18 SVG/img per layout tier. Bottom-
     playlist sits between the embed (18) and fullscreen (48) sizes.
     For raster <img> uploads the asset's native aspect ratio may not
     be perfectly 1:1 even though the form asks for square — forcing
     both width AND height stretched the logo. Use object-fit: contain
     so the image scales to fit the box while preserving its native
     aspect ratio, letterboxing transparent space around it. SVGs are
     unaffected (browsers letterbox via preserveAspectRatio). */
  & img,
  & svg {
    width: ${({ $fs, $bp }) =>
      pickTier($fs, $bp, "64px", "48px", "24px")} !important;
    height: ${({ $fs, $bp }) =>
      pickTier($fs, $bp, "64px", "48px", "24px")} !important;
    object-fit: contain;
  }
`;

const CopyBlock = styled(Flex)`
  flex: 1 1 auto;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const TitleText = styled(Text)<{ $fs?: boolean; $bp?: boolean }>`
  color: #fff;
  font-size: ${({ $fs, $bp }) => pickTier($fs, $bp, "29px", "23px", "12px")};
  font-weight: 700;
  line-height: ${({ $fs, $bp }) => pickTier($fs, $bp, "37px", "28px", "15px")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubtitleText = styled(Text)<{ $fs?: boolean; $bp?: boolean }>`
  color: rgba(255, 255, 255, 0.6);
  font-size: ${({ $fs, $bp }) => pickTier($fs, $bp, "21px", "17px", "9px")};
  font-weight: 500;
  line-height: ${({ $fs, $bp }) => pickTier($fs, $bp, "27px", "21px", "12px")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CtaPill = styled.a<{
  $bg: string;
  $color: string;
  $fs?: boolean;
  $bp?: boolean;
}>`
  align-items: center;
  background: ${({ $bg }) => $bg};
  border-radius: 999px;
  color: ${({ $color }) => $color};
  display: inline-flex;
  flex: 0 0 auto;
  font-size: ${({ $fs, $bp }) => pickTier($fs, $bp, "21px", "17px", "9px")};
  font-weight: 700;
  height: ${({ $fs, $bp }) => pickTier($fs, $bp, "40px", "32px", "17px")};
  letter-spacing: 0.1px;
  padding: ${({ $fs, $bp }) => pickTier($fs, $bp, "0 19px", "0 16px", "0 7px")};
  text-decoration: none;
  transition:
    filter 120ms ease,
    transform 120ms ease;

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }
`;

// Negative left margin pulls the dismiss button in toward the CTA pill,
// overriding the parent BannerShell `gap` for this single edge. Visual
// intent per latest review: CTA and × read as a tight action pair, not
// as two separate elements separated by the same gap as the icon/copy.
const DismissBtn = styled.button<{
  $ready: boolean;
  $fs?: boolean;
  $bp?: boolean;
}>`
  align-items: center;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: ${({ $fs, $bp }) => pickTier($fs, $bp, "29px", "21px", "13px")};
  height: ${({ $fs, $bp }) => pickTier($fs, $bp, "35px", "24px", "16px")};
  justify-content: center;
  line-height: 1;
  margin-left: -2px;
  opacity: ${({ $ready }) => ($ready ? 1 : 0)};
  pointer-events: ${({ $ready }) => ($ready ? "auto" : "none")};
  padding: 0;
  transition:
    opacity 200ms ease,
    color 120ms ease;
  width: ${({ $fs, $bp }) => pickTier($fs, $bp, "35px", "24px", "16px")};

  &:hover {
    color: #fff;
  }
`;

export const TopBannerSlot = () => {
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const phase = useRecoilValue(BrandedPlaybackPhaseState);
  const elapsed = useRecoilValue(BrandedPlaybackElapsedMsState);
  const isFullscreen = useRecoilValue(BrandedFullscreenState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";
  const [dismissed, setDismissed] = useRecoilState(
    BrandedTopBannerDismissedState,
  );
  const viewerGeo = useRecoilValue(BrandedViewerGeoState);

  const banner = sponsor?.slots.topBanner;

  // Banner impression only counts once the slide-in has actually played —
  // pre-appear, the element is off-screen anyway, but the IO threshold-only
  // gate is faster than scheduling our own dwell logic for the appearAfterMs
  // window. We gate the hook via `enabled` on the banner's visibility flag.
  const impressionRef = useImpressionBeacon({
    slot: "topBanner",
    creativeId: banner?.id,
    enabled: !!banner,
  });

  const handleCtaClick = () => {
    if (!banner) return;
    fireAdClickBeacon({
      campaignId: sponsor?.campaignId,
      slot: "topBanner",
      creativeId: banner.id,
      viewerGeo,
    });
  };

  if (!banner) return null;

  const visible = computeTopBannerVisible(
    elapsed,
    phase,
    banner.appearAfterMs,
    dismissed,
  );
  const dismissReady =
    !!banner.dismissable &&
    computeTopBannerDismissReady(
      elapsed,
      banner.appearAfterMs,
      banner.dismissTimeoutMs,
    );

  const ctaBg = banner.ctaBgColor ?? sponsor.theme?.primary ?? "#FF2864";
  const ctaColor = banner.ctaTextColor ?? "#fff";

  /**
   * Image-only mode: campaign uploaded a complete pre-composed creative
   * (gif/png/jpg) and left BOTH the title and ctaLabel empty. Render the
   * uploaded `assetUrl` as the entire banner — no icon tile, no
   * rich-text composition, no separate CTA pill. The whole shell is the
   * click target. Inferred from data (no schema flag) so campaigns can
   * toggle modes by just clearing/filling the copy fields.
   */
  const hasTitle = !!banner.title && banner.title.trim().length > 0;
  const hasCtaLabel = !!banner.ctaLabel && banner.ctaLabel.trim().length > 0;
  const isImageOnly = !!banner.assetUrl && !hasTitle && !hasCtaLabel;

  if (isImageOnly) {
    return (
      <ImageOnlyShell
        href={banner.ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={banner.altText}
        $visible={visible}
        $fs={isFullscreen}
        $bp={isBottomPlaylist}
        ref={impressionRef}
        onClick={handleCtaClick}
      >
        {banner.dismissable && (
          <ImageOnlyDismissBtn
            type="button"
            $ready={dismissReady}
            aria-label="Dismiss banner"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDismissed(true);
            }}
          >
            ×
          </ImageOnlyDismissBtn>
        )}
        <img src={banner.assetUrl} alt={banner.altText} />
      </ImageOnlyShell>
    );
  }

  return (
    <BannerShell
      $visible={visible}
      $fs={isFullscreen}
      $bp={isBottomPlaylist}
      aria-hidden={!visible}
      ref={impressionRef}
    >
      <IconTileSlot $fs={isFullscreen} $bp={isBottomPlaylist}>
        <SponsorLogo
          creative={banner}
          variant="iconTile"
          width={18}
          height={18}
        />
      </IconTileSlot>
      <CopyBlock>
        <TitleText $fs={isFullscreen} $bp={isBottomPlaylist}>
          {banner.title}
        </TitleText>
        {banner.subtitle && (
          <SubtitleText $fs={isFullscreen} $bp={isBottomPlaylist}>
            {banner.subtitle}
          </SubtitleText>
        )}
      </CopyBlock>
      <CtaPill
        href={banner.ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        $bg={ctaBg}
        $color={ctaColor}
        $fs={isFullscreen}
        $bp={isBottomPlaylist}
        onClick={handleCtaClick}
      >
        {banner.ctaLabel}
      </CtaPill>
      {banner.dismissable && (
        <DismissBtn
          $ready={dismissReady}
          $fs={isFullscreen}
          $bp={isBottomPlaylist}
          aria-label="Dismiss banner"
          onClick={() => setDismissed(true)}
        >
          ×
        </DismissBtn>
      )}
    </BannerShell>
  );
};
